<i18n locale="en">
{
  "cta": "Order Now",
  "empty-title": "Menu Item",
  "errors": {
    "load": "Unable to load item"
  }
}
</i18n>

<template>
  <PreviewSheet background="primary" radius="0" class="px-4 py-3">
    <PreviewSheet
      border="none"
      background="secondary"
      class="item-preview__container"
      :placeholder="!item"
    >
      <div v-if="isLoading" class="flex-row align-center justify-center">
        <OwnProgressSpinner size="24" />
      </div>
      <div v-else class="flex-row h-full w-full">
        <PreviewSheet
          v-if="item"
          background="primary"
          class="flex-row h-full w-full"
        >
          <img
            v-if="item.image"
            :alt="item.displayName"
            :src="item.image"
            class="item-preview__image h-full"
          />
          <div class="flex-1 flex-col justify-between px-6 py-6">
            <div class="flex-col">
              <PreviewTypography
                :text="item.displayName ?? item.name"
                color="brand"
                variant="titleSmall"
                class="item-preview__title"
              />
              <PreviewTypography
                v-if="item.description"
                :text="
                  item.description.length > 50
                    ? item.description.substring(0, 50 - 3) + '...'
                    : item.description
                "
                color="secondary"
                variant="paragraphSmall"
              />
            </div>
          </div>
        </PreviewSheet>
        <div
          v-else
          background="secondary"
          class="pa-6 flex-col align-center justify-center h-full w-full"
        >
          <PreviewIcon color="secondary" size="24" :icon="emptyIcon" />
          <PreviewTypography
            :text="t('empty-title')"
            color="secondary"
            variant="paragraphSmall"
          />
        </div>
      </div>
    </PreviewSheet>
  </PreviewSheet>
</template>

<script>
import { PhForkKnife } from '@phosphor-icons/vue'
import { isDefined } from '@vueuse/core'
import { markRaw } from 'vue'
import { useI18n } from 'vue-i18n'

import PreviewIcon from '@/lib/builder/public/preview/PreviewIcon.vue'
import PreviewSheet from '@/lib/builder/public/preview/PreviewSheet.vue'
import PreviewTypography from '@/lib/builder/public/preview/PreviewTypography.vue'
import notify from '@/mixins/notify'
import { OlympusClient } from '@/OlympusClient'
import { OwnProgressSpinner } from '@/ui/OwnProgressSpinner'

export default {
  name: 'ItemPreview',
  components: {
    OwnProgressSpinner,
    PreviewIcon,
    PreviewSheet,
    PreviewTypography,
  },
  mixins: [notify],
  props: {
    content: { type: Object, required: true },
  },
  setup() {
    const { t } = useI18n()

    return { t }
  },
  data() {
    return {
      emptyIcon: markRaw(PhForkKnife),
      isLoading: false,
      item: null,
    }
  },
  computed: {
    itemId() {
      const { content } = this

      return content?.data?.params?.itemId
    },
  },
  watch: {
    itemId: 'loadItem',
  },
  async mounted() {
    await this.loadItem()
  },
  methods: {
    async loadItem() {
      if (!isDefined(this.itemId)) return
      try {
        this.isLoading = true
        this.item = await OlympusClient.items.item.get({
          params: { menuItemId: this.itemId },
        })
      } catch (error) {
        this.$notify(this.t('errors.load'), 'error')
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.item-preview {
  &__container {
    height: 100px;
  }

  &__image {
    height: 100px;
    width: 100px;
    border-radius: 16px;
  }

  &__title {
    text-decoration: underline;
  }
}
</style>
